import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useGetInmopublicMembershipsQuery } from './inmopublicMembershipsApi';
import { Search, ChevronLeft, ChevronRight, Users, UserCheck } from 'lucide-react';
import type { InmopublicMembership } from '@/types/inmopublic-memberships.types';
import { CreateMembershipDialog } from './components/CreateMembershipDialog';

const limit = 10;

const formatDate = (date: Date | string | undefined) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'invited':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'suspended':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'left':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getRoleColor = (role: string) => {
  switch (role?.toLowerCase()) {
    case 'admin':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'member':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const InmopublicMemberships = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useGetInmopublicMembershipsQuery({
    page: currentPage,
    limit,
    text: searchText || undefined,
  });

  const totalPages = data ? Math.ceil(data.totalMemberships / limit) : 0;

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchText('');
    setSearchInput('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    handleSearch();
  };

  return (
    <>
      {/* Memberships Overview */}
      <div className='mb-8'>
        <h2 className='text-3xl font-bold text-white mb-2'>Inmopublic Memberships Management</h2>
        <p className='text-slate-400'>Manage user memberships across Inmopublic accounts.</p>
      </div>

      {/* Stats and Search */}
      <div className='grid gap-6 md:grid-cols-3 mb-6'>
        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Total Memberships</p>
                <p className='text-3xl font-bold text-white'>{isLoading ? '...' : data?.totalMemberships || 0}</p>
              </div>
              <Users className='h-8 w-8 text-slate-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Active Memberships</p>
                <p className='text-3xl font-bold text-white'>{isLoading ? '...' : (data?.activeMemberships ?? 0)}</p>
              </div>
              <UserCheck className='h-8 w-8 text-green-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center gap-3'>
              <Input
                placeholder='Search memberships...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className='bg-slate-700 border-slate-600 text-white placeholder:text-slate-400'
              />
              <Button onClick={handleSearch} size='sm' disabled={isLoading} className='flex-shrink-0'>
                <Search className='h-4 w-4' />
              </Button>
              {searchText && (
                <Button onClick={handleClearSearch} variant='outline' size='sm' className='flex-shrink-0'>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Membership Button */}
      <div className='mb-6'>
        <CreateMembershipDialog />
      </div>

      {/* Memberships Table */}
      <Card className='bg-slate-800 border-slate-700'>
        <CardHeader>
          <CardTitle className='text-white'>
            Memberships List
            {searchText && (
              <span className='text-sm font-normal text-slate-400 ml-2'>(filtered by "{searchText}")</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-slate-400'>Loading memberships...</div>
            </div>
          ) : error ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-red-400'>Error loading memberships</div>
            </div>
          ) : !data?.memberships?.length ? (
            <div className='flex items-center justify-center py-8'>
              <div className='text-slate-400'>
                {searchText ? 'No memberships found matching your search' : 'No memberships found'}
              </div>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead>
                    <tr className='border-b border-slate-700'>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>User</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Account</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Role</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Status</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Joined</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Invited By</th>
                      <th className='text-left py-3 px-2 text-slate-300 font-medium'>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.memberships.map((membership: InmopublicMembership) => (
                      <tr key={membership._id} className='border-b border-slate-700/50 hover:bg-slate-700/25'>
                        <td className='py-4 px-2'>
                          <div>
                            {membership.user ? (
                              <>
                                <div className='text-white font-medium'>
                                  {membership.user.firstName} {membership.user.lastName}
                                </div>
                                <div className='text-sm text-slate-400'>{membership.user.email}</div>
                              </>
                            ) : (
                              <div className='text-red-400'>User not found</div>
                            )}
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div>
                            {membership.account ? (
                              <>
                                <div className='text-white font-medium'>{membership.account.name}</div>
                                <div className='text-sm text-slate-400'>{membership.account.municipality}</div>
                              </>
                            ) : (
                              <div className='text-red-400'>Account not found</div>
                            )}
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <Badge className={getRoleColor(membership.role)}>{membership.role.toLowerCase()}</Badge>
                        </td>
                        <td className='py-4 px-2'>
                          <Badge className={getStatusColor(membership.status)}>{membership.status.toLowerCase()}</Badge>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{formatDate(membership.joinedAt)}</div>
                        </td>
                        <td className='py-4 px-2'>
                          <div>
                            {membership.inviter ? (
                              <>
                                <div className='text-white font-medium'>
                                  {membership.inviter.firstName} {membership.inviter.lastName}
                                </div>
                                <div className='text-sm text-slate-400'>{membership.inviter.email}</div>
                              </>
                            ) : (
                              <div className='text-slate-400'>N/A</div>
                            )}
                          </div>
                        </td>
                        <td className='py-4 px-2'>
                          <div className='text-slate-300'>{formatDate(membership.createdAt)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex items-center justify-between mt-6 pt-4 border-t border-slate-700'>
                  <div className='text-sm text-slate-400'>
                    Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, data.totalMemberships)} of{' '}
                    {data.totalMemberships} memberships
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage <= 1 || isLoading}
                    >
                      <ChevronLeft className='h-4 w-4' />
                      Previous
                    </Button>

                    <div className='flex items-center gap-1'>
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                          return (
                            <Button
                              key={page}
                              variant={currentPage === page ? 'default' : 'outline'}
                              size='sm'
                              onClick={() => setCurrentPage(page)}
                              disabled={isLoading}
                              className='w-8 h-8 p-0'
                            >
                              {page}
                            </Button>
                          );
                        } else if (page === currentPage - 3 || page === currentPage + 3) {
                          return (
                            <span key={page} className='text-slate-400'>
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage >= totalPages || isLoading}
                    >
                      Next
                      <ChevronRight className='h-4 w-4' />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};
